import { type Ref, ref } from 'vue'
import {
  downloadProofOfPlayExport,
  type ProofOfPlayMetricsResponse,
} from '@/helpers/api/metrics'
import useNotifications from '@/components/Notifications/composables/notifications'

export interface ExportSections {
  summary: boolean
  byMedia: boolean
  byPlayer: boolean
  timeSeries: boolean
}

const MIN_EXPORT_MS = 1500

/**
 * Export composable for the Reports view.
 *
 * Previously this built the workbook client-side via the `xlsx` package.
 * It now hands every export off to the backend's streaming endpoint
 * (see CODE_REVIEW.md §3.2 and metrics.ts → downloadProofOfPlayExport).
 *
 * The `proofOfPlayResponse` ref is still accepted so callers can disable the
 * export buttons until data has loaded — but its rows aren't sent over the
 * wire any more. Backend re-queries with the filter params we pass.
 */
export function useReportExport(
  proofOfPlayResponse: Ref<ProofOfPlayMetricsResponse | null>,
  buildCurrentFilterParams: () => Record<string, any>,
) {
  const notification = useNotifications()
  const isExporting = ref(false)

  async function runExport(fn: () => Promise<void>) {
    isExporting.value = true
    const start = Date.now()
    try {
      await fn()
      // Hold the busy state long enough for the spinner to feel intentional
      // rather than flashing on/off for sub-second downloads.
      const elapsed = Date.now() - start
      if (elapsed < MIN_EXPORT_MS) {
        await new Promise((r) => setTimeout(r, MIN_EXPORT_MS - elapsed))
      }
      notification.success('Export complete!')
    } catch (error) {
      console.error('[useReportExport] export failed:', error)
      notification.failure('Export failed. Please try again.')
    } finally {
      isExporting.value = false
    }
  }

  function sectionsCsv(sections: ExportSections): string {
    const enabled: string[] = []
    if (sections.summary) enabled.push('summary')
    if (sections.byMedia) enabled.push('byMedia')
    if (sections.byPlayer) enabled.push('byPlayer')
    if (sections.timeSeries) enabled.push('timeSeries')
    return enabled.join(',')
  }

  // buildCurrentFilterParams gives us { timeRange, accountId?, playerUuids?,
  // mediaIds? } — exactly the shape ProofOfPlayExportParams expects, minus
  // `sections` which the caller supplies.
  async function exportSections(sectionsValue: string) {
    if (!proofOfPlayResponse.value) return
    await runExport(async () => {
      const params = buildCurrentFilterParams() as Record<string, any>
      if (sectionsValue) params.sections = sectionsValue
      await downloadProofOfPlayExport(params)
    })
  }

  async function exportToExcel(sections: ExportSections) {
    const csv = sectionsCsv(sections)
    if (!csv) return // user unchecked everything
    await exportSections(csv)
  }

  async function exportSectionMedia() {
    await exportSections('byMedia')
  }

  async function exportSectionPlayer() {
    await exportSections('byPlayer')
  }

  async function exportSectionTrend() {
    await exportSections('timeSeries')
  }

  return {
    isExporting,
    exportToExcel,
    exportSectionMedia,
    exportSectionPlayer,
    exportSectionTrend,
  }
}
